import { SingleAudioPlayer } from "shared/AudioPlayers/SingleAudioPlayer";
import { storagePath } from "apiRoutes/storagePath";
import {
  Looop,
  LooopAudioFiles,
  LooopInstrument,
  loopAudioFiles,
} from "shared/games/Looop";
import { audioCtx } from "util/audioContext";
import { InstrumentPlayerSyncParams } from "./types";
import { PlaybackControls } from "../types";

type SourcesNeededConfig = typeof loopAudioFiles[number][];

export class InstrumentPlayer {
  private instrument: LooopInstrument;
  private looop: Looop;
  private sourcesNeeded: SourcesNeededConfig;
  private allPlayers: SingleAudioPlayer[] = [];
  private players: {
    [file in keyof LooopAudioFiles]?: SingleAudioPlayer;
  } = {};
  private userProvidedDefaultSyncParams?: Partial<InstrumentPlayerSyncParams>;

  constructor(
    instrument: LooopInstrument,
    looop: Looop,
    sourcesNeeded: SourcesNeededConfig = [...loopAudioFiles],
    defaultSyncParams?: Partial<InstrumentPlayerSyncParams>
  ) {
    this.instrument = instrument;
    this.looop = looop;
    this.sourcesNeeded = sourcesNeeded;
    this.userProvidedDefaultSyncParams = defaultSyncParams;
  }

  public load = async () => {
    const audioFiles =
      Array.isArray(this.sourcesNeeded) && this.sourcesNeeded.length > 0
        ? this.sourcesNeeded
        : loopAudioFiles;
    const instrument = this.instrument.instrument;

    audioFiles.forEach((audioFile) => {
      const url = storagePath(
        `${this.looop.base_audio_url}${this.looop.instruments[instrument].audio_files[audioFile]}`
      );

      const player = new SingleAudioPlayer(url, this.looop);
      const defaultSyncParams = this.defaultSyncParams[audioFile].controls;

      if (defaultSyncParams) {
        player.setPlaybackStatus(defaultSyncParams, audioCtx().currentTime);
      }

      this.players = {
        ...this.players,
        [audioFile]: player,
      };

      this.allPlayers.push(player);
    });

    console.log("Loading...");

    await Promise.all(this.allPlayers.map((player) => player.load()));

    console.log("Completed loading");
  };

  public start = (start?: number) => {
    const startTime = start ?? audioCtx().currentTime + 0.2;
    this.allPlayers.forEach((player) => player.start(startTime));
  };

  public stop = (end?: number) => {
    const endTime = end ?? audioCtx().currentTime + 0.2;
    this.allPlayers.forEach((player) => player.stop(endTime));
  };

  public dispose = () => {
    this.allPlayers.forEach((player) => player.dispose());
  };

  public sync = (
    params: Partial<InstrumentPlayerSyncParams>,
    time?: number
  ) => {
    const syncTime = time ?? audioCtx().currentTime + 0.4;

    Object.values(params).forEach(({ audioFile, controls }) => {
      const player = this.players[audioFile];

      if (!player) {
        console.log(
          "no player found for audioFile likely wasn't loaded",
          audioFile
        );

        return;
      }

      player.setPlaybackStatus(controls, syncTime);
    });
  };

  // All audio_files that are not passed in are muted by default
  private get defaultSyncParams(): InstrumentPlayerSyncParams {
    const defaultControls: Partial<PlaybackControls> = {
      muted: true,
    };

    const defaultSyncParams: InstrumentPlayerSyncParams = {
      default: {
        audioFile: "default",
        controls:
          this.userProvidedDefaultSyncParams?.default?.controls ??
          defaultControls,
      },
      comp_opto1: {
        audioFile: "comp_opto1",
        controls:
          this.userProvidedDefaultSyncParams?.comp_opto1?.controls ??
          defaultControls,
      },
      comp_opto2: {
        audioFile: "comp_opto2",
        controls:
          this.userProvidedDefaultSyncParams?.comp_opto2?.controls ??
          defaultControls,
      },
      comp_opto4: {
        audioFile: "comp_opto4",
        controls:
          this.userProvidedDefaultSyncParams?.comp_opto4?.controls ??
          defaultControls,
      },
      comp_opto7: {
        audioFile: "comp_opto7",
        controls:
          this.userProvidedDefaultSyncParams?.comp_opto7?.controls ??
          defaultControls,
      },
      comp_opto10: {
        audioFile: "comp_opto10",
        controls:
          this.userProvidedDefaultSyncParams?.comp_opto10?.controls ??
          defaultControls,
      },
    };

    return defaultSyncParams;
  }
}
